import React from "react";
import useInitiateRegService from "./service";
import DynamicFormRenderer from "../../../../../shared/form/renderer";
import Button from "../../../../../shared/components/button";
import FormTitle from "apps/registration/components/formTitle";
import { RegistrationStep } from "shared/utils/enums";
import useAlertWhenRefreshed from "shared/hooks/useAlertWhenRefreshed";
import TopNote from "shared/components/topNote";
import { useCopies } from "shared/hooks/useCopies";
import Icon from "assets/icons";
import Variant from "shared/enums/Variant";
import { isNonAttendeeCompletedOrApprovalPending, isTicketCompletedOrApprovalPending } from "apps/registration/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRegDetail } from "../../../../../shared/context/regDetail";
import PaymentComponentWrapper from "../../../../../shared/components/paymentWrapper";
import { ApiRegistrationParticipantForParticipantResponse } from "shared/types/ApiRegistrationParticipantForParticipantResponse";
import { ApiApplicableRateCardDetails } from "shared/types/ApiApplicableRateCardDetails";
import { InitiateRegServiceObj } from "./types";
import RegistrationFooter from "apps/registration/components/registrationFooter";
import { PurchaseOrderDetails } from "shared/components/purchaseOrder";
import EmailValidationWrapper from "./emailValidationWrapper";
import { getStripeReturnUrl } from "./utils";
import ButtonGroup from "shared/components/button/components/buttonGroup";

export default function InitiateRegistration() {
  const { helpdeskEmail, showCardIcons } = useRegDetail();
  const copies = useCopies();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { participantObj, navigateToConfirmation, applicableRates, regFormDetailObj, ...initiateRegServiceObj } =
    useInitiateRegService();

  React.useEffect(() => {
    if (participantObj) {
      const isTicketCompletedOrInReview = isTicketCompletedOrApprovalPending(participantObj.ticketStatus);
      if (isTicketCompletedOrInReview) {
        navigateToConfirmation(participantObj.id);
        return;
      }
      const isNonAttendeeCompletedOrInReview = isNonAttendeeCompletedOrApprovalPending(
        participantObj.nonAttendeeApplication?.status
      );
      if (isNonAttendeeCompletedOrInReview) {
        navigateToConfirmation(participantObj.id);
        return;
      }
    }
  }, [participantObj, navigateToConfirmation, navigate, helpdeskEmail, copies]);

  return (
    <React.Fragment>
      <div className="registrationWrapper">
        {initiateRegServiceObj.displayNote && (
          <TopNote storageKey={initiateRegServiceObj.pageTitle} body={initiateRegServiceObj.displayNote} />
        )}
        <FormTitle title={initiateRegServiceObj.pageTitle} />
        <div className="registrationFormPage">
          {initiateRegServiceObj.currentStep === RegistrationStep.PAYMENT && (
            <div className="backToDetailsPage">
              <Button
                variant={Variant.LINK}
                icon={<Icon.BackCircle />}
                label={copies.get("back")}
                onClick={() => initiateRegServiceObj.setCurrentStep(RegistrationStep.DETAILS)}
              />
            </div>
          )}
          {initiateRegServiceObj.currentStep === RegistrationStep.EMAIL_VALIDATION && (
            <div className="emailValidationMessage">{copies.get("email.domain.validation.info.message")}</div>
          )}
          <div className="registrationFormWrapper">
            {initiateRegServiceObj.currentStep === RegistrationStep.EMAIL_VALIDATION && (
              <EmailValidationWrapper
                setCurrentStep={initiateRegServiceObj.setCurrentStep}
                participantObj={participantObj}
                regFormDetailObj={regFormDetailObj}
              />
            )}
            {initiateRegServiceObj.currentStep === RegistrationStep.DETAILS && (
              <RegistrationForm
                initiateRegServiceObj={initiateRegServiceObj}
                participantObj={participantObj}
                applicableRates={applicableRates}
              />
            )}
            {initiateRegServiceObj.currentStep === RegistrationStep.PAYMENT && (
              <React.Fragment>
                {participantObj?.regPurchaseOrder && (
                  <div className="regPaymentWrapper">
                    <PaymentComponentWrapper
                      amount={participantObj.regPurchaseOrder.amount}
                      registrationParticipantId={participantObj.id}
                      purchaseOrderId={participantObj.regPurchaseOrder.purchaseOrderId}
                      successHandler={initiateRegServiceObj.paymentSuccessHandler}
                      errorHandler={initiateRegServiceObj.paymentErrorHandler}
                      onAmountMismatch={initiateRegServiceObj.paymentErrorHandler}
                      showCardIcons={showCardIcons}
                      registrationParticipantUiVersion={participantObj.uiVersion}
                      stripeReturnUrl={getStripeReturnUrl(participantObj, searchParams)}
                      invoicePaymentEnabled={initiateRegServiceObj.invoicePaymentEnabled}
                      invoicePaymentConfig={{
                        initialEmail: participantObj.regPurchaseOrder.invoicePaymentRequest?.invoicePayerEmail
                      }}
                      invoicePayerId={participantObj.regPurchaseOrder.invoicePaymentRequest?.invoicePayerId}
                      invoicePaymentRequestId={
                        participantObj.regPurchaseOrder.invoicePaymentRequest?.invoicePaymentRequestId
                      }
                      invoicePayerPaymentRequestSuccessHandler={
                        initiateRegServiceObj.invoicePayerPaymentRequestSuccessHandler
                      }
                      resendInvoicePaymentReminderSuccessHandler={
                        initiateRegServiceObj.resendInvoicePaymentReminderSuccessHandler
                      }
                    />
                    <PurchaseOrderDetails
                      purchaseOrder={participantObj?.regPurchaseOrder}
                      originatingFormName={participantObj?.registrationForm?.title}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
      <RegistrationFooter />
    </React.Fragment>
  );
}

function RegistrationForm({
  initiateRegServiceObj,
  participantObj,
  applicableRates
}: {
  initiateRegServiceObj: InitiateRegServiceObj;
  participantObj: ApiRegistrationParticipantForParticipantResponse;
  applicableRates?: Array<ApiApplicableRateCardDetails>;
}) {
  const { currencyCode } = useRegDetail();
  useAlertWhenRefreshed();

  return (
    <React.Fragment>
      <DynamicFormRenderer
        key={participantObj?.uiVersion}
        formRef={initiateRegServiceObj.formRef}
        schemaList={initiateRegServiceObj.formSchemaList}
        formSubmitHandler={initiateRegServiceObj.submitHandler}
        includeCaptcha={!participantObj}
        parseValuesOnSubmitToApiReq={true}
        disabled={initiateRegServiceObj.isFormDisabled}
        customData={{
          eventSeriesId: initiateRegServiceObj.eventSeriesId,
          registrationId: initiateRegServiceObj.registrationId,
          registrationFormId: initiateRegServiceObj.formId,
          organizationId: participantObj?.organizationId,
          applicableRates,
          currencyCode
        }}
      />
      <ButtonGroup inlineFormButtons>
        <Button
          label={initiateRegServiceObj.submitButtonText}
          onClick={() => {
            if (initiateRegServiceObj.isFormDisabled) {
              initiateRegServiceObj.navigateToApplicableView(participantObj);
            } else {
              initiateRegServiceObj.formRef.current?.submitForm();
            }
          }}
        />
      </ButtonGroup>
    </React.Fragment>
  );
}
